import React, { useEffect, useRef, useState } from "react";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import DehazeOutlinedIcon from "@mui/icons-material/DehazeOutlined";
import GrainIcon from "@mui/icons-material/Grain";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import ThunderstormOutlinedIcon from "@mui/icons-material/ThunderstormOutlined";
import AirIcon from "@mui/icons-material/Air";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import { useWeather } from "./WeatherContext";
import { PREVIEWS } from "./bostonWeather";

// "Boston, MA · 46°F, clear", followed by a small menu for previewing other weather.

const ICONS = {
  clouds: CloudOutlinedIcon,
  fog: DehazeOutlinedIcon,
  drizzle: GrainIcon,
  rain: WaterDropOutlinedIcon,
  snow: AcUnitIcon,
  storm: ThunderstormOutlinedIcon,
  noreaster: AirIcon,
};

function WeatherIcon({ kind, isDay = true }) {
  const Icon = kind === "clear" ? (isDay ? WbSunnyOutlinedIcon : BedtimeOutlinedIcon) : ICONS[kind];
  return Icon ? <Icon fontSize="small" aria-hidden="true" /> : null;
}

const PREVIEW_NAMES = { drizzle: "drizzle", noreaster: "a nor'easter" };
const previewName = (kind) =>
  PREVIEW_NAMES[kind] ?? PREVIEWS.find((p) => p.kind === kind).label.toLowerCase();

function WeatherPicker() {
  const { preview, setPreview, live, scene } = useWeather();
  const [open, setOpen] = useState(false);
  const root = useRef(null);

  // Close on a click elsewhere or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!root.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const choose = (kind) => {
    setPreview(kind);
    setOpen(false);
  };

  const pill =
    "inline-flex items-center gap-1 h-6 text-sm font-medium text-[#eb4d5c] dark:text-[#c9a7ff] hover:bg-[#eb4d5c]/10 dark:hover:bg-[#c9a7ff]/10 transition-colors";
  const option =
    "flex items-center gap-2 w-full rounded-lg px-3 py-1.5 text-left text-base text-[#333] dark:text-[#e4e7ef] hover:bg-[#f0f0f0] dark:hover:bg-[#3d4851]";

  return (
    <div ref={root} className="relative">
      {/* A block-level flex box, so its position does not follow the text baseline as the label changes */}
      <div className="flex items-center rounded-full border border-[#eb4d5c]/60 dark:border-[#c9a7ff]/60 overflow-hidden">
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={`${pill} pl-2.5 ${preview ? "pr-1.5" : "pr-2"}`}
        >
          {preview ? (
            <>
              <WeatherIcon kind={preview} isDay={scene.isDay} />
              Previewing {previewName(preview)}
            </>
          ) : (
            "Try other weather"
          )}
          <ExpandMoreIcon fontSize="small" aria-hidden="true" />
        </button>
        {preview && (
          <button
            type="button"
            onClick={() => choose(null)}
            aria-label="Back to Boston's live weather"
            title="Back to Boston's live weather"
            className={`${pill} pl-1 pr-2 border-l border-[#eb4d5c]/40 dark:border-[#c9a7ff]/40`}
          >
            <CloseIcon fontSize="small" aria-hidden="true" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-30 top-full mt-2 left-1/2 -translate-x-1/2 lg:left-0 lg:translate-x-0 w-52 rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#2b343c] p-1.5 shadow-xl">
          <button type="button" aria-pressed={!preview} onClick={() => choose(null)} className={option}>
            <MyLocationOutlinedIcon fontSize="small" aria-hidden="true" />
            <span className="flex-grow">{live ? "Live in Boston" : "Boston (unavailable)"}</span>
            {!preview && <span aria-hidden="true">✓</span>}
          </button>
          <div className="my-1 border-t border-black/10 dark:border-white/10" />
          {PREVIEWS.map((p) => (
            <button
              key={p.kind}
              type="button"
              aria-pressed={preview === p.kind}
              onClick={() => choose(p.kind)}
              className={option}
            >
              <WeatherIcon kind={p.kind} isDay={live ? live.isDay : true} />
              <span className="flex-grow">{p.label}</span>
              {preview === p.kind && <span aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WeatherLine() {
  const weather = useWeather();
  const live = weather?.live;

  // Fades the line in once the forecast settles, so nothing in it moves where it
  // can be seen. When the forecast was already saved it is there from the first paint.
  const settled = Boolean(weather?.settled);
  const [shown, setShown] = useState(settled);
  useEffect(() => {
    if (!settled || shown) return undefined;
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, [settled, shown]);
  const fade = `motion-safe:transition-opacity motion-safe:duration-500 ${shown ? "opacity-100" : "opacity-0 pointer-events-none"}`;

  return (
    // On narrow screens the menu always takes its own row, so the forecast arriving cannot re-wrap the line.
    <div className={`flex flex-col lg:flex-row lg:flex-wrap items-center justify-center lg:justify-start gap-x-3 gap-y-2 text-lg text-[#555] dark:text-[#c9cfe3] pb-4 ${fade}`}>
      <p className="flex items-center gap-1">
        <LocationOnOutlinedIcon fontSize="small" aria-hidden="true" />
        Boston, MA
        {live && (
          <span className="flex items-center gap-1" title="Live weather in Boston right now">
            <span aria-hidden="true" className="px-1">·</span>
            <WeatherIcon kind={live.scene.kind} isDay={live.isDay} />
            {live.tempF}°F, {live.label}
            {live.noreaster && <span className="font-semibold">&nbsp;· nor&apos;easter!</span>}
          </span>
        )}
      </p>
      {weather && <WeatherPicker />}
    </div>
  );
}
